import { useEffect, useMemo, useState } from "react";
import { admin } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Database, Play, Loader2, AlertTriangle, Search, ChevronDown, ChevronRight, Copy, Server } from "lucide-react";

const PRESET_QUERIES = [
  { label: "Top 10 recent PRs", sql: "SELECT TOP 10 Voc_no, Vdate, Pcode, ReqStatus, Amount, Requestor, Auth_by, App_by FROM PoRequest ORDER BY Vdate DESC, Voc_no DESC" },
  { label: "Items on latest PR", sql: "SELECT TOP 20 i.Voc_no, i.icode, i.qty, i.Rate, i.LastRate FROM PoRequesti i WHERE i.Voc_no = (SELECT TOP 1 Voc_no FROM PoRequest ORDER BY Vdate DESC)" },
  { label: "Active ULogin users", sql: "SELECT RTRIM(Uname) Uname, RTRIM(FullName) FullName, RTRIM(Emp_no) Emp_no, Isactive FROM ULogin WHERE Isactive IN ('Y','Active','1','active')" },
  { label: "Supplier search (party)", sql: "SELECT TOP 20 RTRIM(pcode) pcode, RTRIM(party) party FROM party WHERE party LIKE '%R-pac%'" },
  { label: "Price history for an item", sql: "SELECT TOP 20 i.purc_no, i.icode, i.rate, p.vdate, p.pcode FROM GPurci i JOIN GPurc p ON i.purc_no=p.purc_no AND i.compcode=p.compcode WHERE RTRIM(i.icode)='BOX1942WM' ORDER BY p.vdate DESC" },
  { label: "PR status counts (last 60d)", sql: "SELECT ReqStatus, COUNT(*) AS n FROM PoRequest WHERE Vdate >= DATEADD(DAY,-60,GETDATE()) GROUP BY ReqStatus ORDER BY n DESC" },
];

export default function DbConsole() {
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [openTable, setOpenTable] = useState(null);
  const [sql, setSql] = useState("SELECT TOP 10 Voc_no, Vdate, Pcode, ReqStatus, Amount FROM PoRequest ORDER BY Vdate DESC");
  const [allowWrites, setAllowWrites] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [pendingWrite, setPendingWrite] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    admin.dbInfo()
      .then(({ data }) => setInfo(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load DB info"))
      .finally(() => setLoadingInfo(false));
  }, []);

  const filteredTables = useMemo(() => {
    if (!info?.mssql?.tables) return [];
    const q = filter.toLowerCase();
    return info.mssql.tables.filter((t) =>
      !q || t.name.toLowerCase().includes(q) || (t.columns || []).some((c) => c.name.toLowerCase().includes(q))
    );
  }, [info, filter]);

  const isWriteQuery = () => {
    const h = sql.trim().toLowerCase();
    return !(h.startsWith("select") || h.startsWith("with"));
  };

  const run = async () => {
    setError(null); setResult(null);
    if (!sql.trim()) return;
    const writeMode = isWriteQuery();
    if (writeMode && !allowWrites) {
      toast.error("Toggle 'Allow writes' to run UPDATE/INSERT/DELETE.");
      return;
    }
    if (writeMode) { setPendingWrite(true); return; }
    await execute(false);
  };

  const execute = async (write) => {
    setRunning(true);
    try {
      const { data } = write ? await admin.dbExec(sql) : await admin.dbQuery(sql);
      setResult({ ...data, write });
      if (write) toast.success(`Affected ${data.affected_rows} row(s) in ${data.elapsed_ms}ms`);
      else toast.success(`${data.row_count} rows in ${data.elapsed_ms}ms`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Query failed";
      setError(msg); toast.error(msg);
    } finally { setRunning(false); setPendingWrite(false); }
  };

  return (
    <div className="space-y-4">
      {/* Connection summary */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-sm p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Server size={11} /> MS SQL</div>
          <div className="font-mono text-sm mt-1">{info?.mssql?.server || "—"}</div>
          <div className="text-xs text-slate-300">DB: <span className="font-mono">{info?.mssql?.database || "—"}</span></div>
          <div className="text-xs text-slate-300">User: <span className="font-mono">{info?.mssql?.user || "—"}</span></div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Database size={11} /> Tables in use</div>
          <div className="text-3xl font-extrabold num mt-1">{info?.mssql?.tables?.length || 0}</div>
          <div className="text-xs text-slate-300">PoRequest · PoRequesti · GPurc · GPurci · ULogin · emp · party</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Database size={11} /> MongoDB</div>
          <div className="font-mono text-sm mt-1">{info?.mongo?.db_name || "—"}</div>
          <div className="text-xs text-slate-300 mt-1">
            {(info?.mongo?.collections || []).map((c) => (
              <span key={c.name} className="inline-block mr-2"><span className="font-mono text-slate-100">{c.name}</span>: {c.count ?? "?"}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Schema browser */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-800">Schema browser</div>
            <div className="text-xs text-slate-500">Tables that the app reads from / writes to. Click to expand columns.</div>
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
            <Input data-testid="db-filter" placeholder="Filter tables / columns…" value={filter} onChange={(e) => setFilter(e.target.value)} className="pl-9 w-64" />
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {loadingInfo && <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading schema…</div>}
          {filteredTables.map((t) => (
            <div key={t.name}>
              <button
                onClick={() => setOpenTable(openTable === t.name ? null : t.name)}
                data-testid={`schema-${t.name}`}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-cyan-50/50 text-left"
              >
                <div className="flex items-center gap-2">
                  {openTable === t.name ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <span className="font-mono text-sm font-semibold text-cyan-800">{t.name}</span>
                  <span className="text-xs text-slate-500">{t.columns?.length || 0} cols</span>
                </div>
                <div className="text-xs text-slate-600">
                  <span className="num font-semibold">{t.row_count?.toLocaleString() ?? "?"}</span> rows
                </div>
              </button>
              {openTable === t.name && (
                <div className="px-4 pb-4 pt-1 bg-slate-50/50">
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-2 py-1.5">Column</th>
                        <th className="text-left px-2 py-1.5">Type</th>
                        <th className="text-left px-2 py-1.5">Nullable</th>
                        <th className="text-right px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.columns.map((c) => (
                        <tr key={c.name} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 font-mono font-semibold text-slate-800">{c.name}</td>
                          <td className="px-2 py-1.5 font-mono text-slate-600">{c.type}</td>
                          <td className="px-2 py-1.5"><span className={`text-[10px] px-1.5 py-0.5 rounded ${c.nullable ? "bg-slate-200 text-slate-700" : "bg-emerald-100 text-emerald-800"}`}>{c.nullable ? "NULL" : "NOT NULL"}</span></td>
                          <td className="px-2 py-1.5 text-right">
                            <button onClick={() => navigator.clipboard.writeText(c.name)} className="text-slate-400 hover:text-slate-700" title="Copy column name"><Copy size={11} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-2 flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setSql(`SELECT TOP 10 * FROM ${t.name}`)} data-testid={`preset-${t.name}`}>
                      Preview rows
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setSql(`SELECT COUNT(*) AS n FROM ${t.name}`)}>
                      Count
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* SQL runner */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Database size={14} /> SQL Test Runner</div>
          <div className="text-xs text-slate-500">SELECT/WITH runs in read-only mode. Toggle &ldquo;Allow writes&rdquo; for UPDATE/INSERT/DELETE (requires confirm).</div>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2 flex-wrap text-xs">
            <span className="text-slate-500 font-semibold uppercase tracking-wider mr-1 self-center">Presets:</span>
            {PRESET_QUERIES.map((p, i) => (
              <button key={p.label} onClick={() => setSql(p.sql)} data-testid={`preset-q-${i}`} className="px-2 py-1 rounded-md bg-slate-100 hover:bg-cyan-100 hover:text-cyan-800 text-slate-700 transition-colors">{p.label}</button>
            ))}
          </div>
          <Textarea
            data-testid="sql-input"
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            rows={6}
            spellCheck={false}
            className="font-mono text-sm"
          />
          <div className="flex items-center justify-between flex-wrap gap-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={allowWrites}
                onChange={(e) => setAllowWrites(e.target.checked)}
                data-testid="allow-writes"
                className="accent-rose-600"
              />
              <span className={allowWrites ? "text-rose-700 font-semibold" : "text-slate-600"}>
                <AlertTriangle size={12} className="inline mr-1" /> Allow writes (UPDATE / INSERT / DELETE)
              </span>
            </label>
            <Button onClick={run} disabled={running || !sql.trim()} data-testid="run-sql" className="bg-cyan-700 hover:bg-cyan-600">
              {running ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1" />}
              Run query
            </Button>
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800">
              <div className="font-semibold mb-1 flex items-center gap-1"><AlertTriangle size={13} /> Query failed</div>
              <div className="font-mono text-xs whitespace-pre-wrap">{error}</div>
            </div>
          )}

          {result && (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="bg-slate-50 px-3 py-2 text-xs text-slate-600 flex items-center justify-between">
                <span>
                  {result.write
                    ? <>Write executed — <b className="num text-slate-900">{result.affected_rows}</b> row(s) affected</>
                    : <>Returned <b className="num text-slate-900">{result.row_count}</b> rows (limit {result.limit})</>}
                </span>
                <span className="text-slate-500">{result.elapsed_ms} ms</span>
              </div>
              {!result.write && (result.rows?.length || 0) > 0 && (
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto" data-testid="sql-result">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        {result.columns.map((c) => (
                          <th key={c} className="text-left px-3 py-2 font-semibold text-slate-700 whitespace-nowrap">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((r, i) => (
                        <tr key={i} className="border-t border-slate-100 hover:bg-cyan-50/30">
                          {result.columns.map((c) => (
                            <td key={c} className="px-3 py-1.5 font-mono whitespace-nowrap max-w-[260px] truncate" title={String(r[c] ?? "")}>
                              {r[c] === null ? <span className="text-slate-400 italic">NULL</span> : String(r[c])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!result.write && result.rows?.length === 0 && (
                <div className="p-6 text-center text-slate-400 text-sm">No rows returned.</div>
              )}
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={pendingWrite} onOpenChange={(o) => !o && setPendingWrite(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle size={18} /> Confirm write to live DB</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to execute the following statement against the <b>production MS SQL database</b>. This action cannot be undone.
              <div className="mt-3 bg-slate-900 text-slate-100 rounded p-3 font-mono text-xs whitespace-pre-wrap max-h-48 overflow-y-auto">{sql}</div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-write">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => execute(true)} className="bg-rose-700 hover:bg-rose-800" data-testid="confirm-write">
              Yes, execute write
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
